import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, Building2, GraduationCap, Gavel, HeartPulse, Phone, Mail, Send } from 'lucide-react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import ReferralFormModal from '../../components/ReferralFormModal';

const PARTNER_TYPES = [
  { icon: Building2, label: 'Hospitals & Emergency Departments' },
  { icon: GraduationCap, label: 'Schools & School Counselors' },
  { icon: Gavel, label: 'Courts & Juvenile Justice Programs' },
  { icon: HeartPulse, label: 'Primary Care & Specialty Providers' },
  { icon: Handshake, label: 'Community & Faith-Based Organizations' },
  { icon: Building2, label: 'Veteran Service Organizations' }
];

const PartnerWithSdoraPage = () => {
  const [showReferralForm, setShowReferralForm] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Partner With SDORA Wellbeing"
        description="Hospitals, schools, courts, primary care providers, and community organizations can partner with SDORA Wellbeing to coordinate mental health referrals in Pikesville and Baltimore County, MD."
        path="/providers/partner"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="partner-with-sdora-title">
            Partner With SDORA Wellbeing
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl">
            We collaborate with hospitals, schools, courts, and community organizations to connect clients with
            timely, accredited mental health care.
          </p>
        </div>
      </div>

      {/* Why Partner */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Partner With Us</h2>
          <p className="text-gray-700 text-center max-w-2xl mx-auto mb-10">
            Referring a client is often the hardest part of connecting them to care. We make it easy by handling
            scheduling, insurance verification, and ongoing communication, so you can stay focused on your own
            caseload while trusting that your client is receiving accredited, evidence-based behavioral health
            treatment close to home in Pikesville.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-center">
            <div className="bg-emerald-50 rounded-lg p-6 border border-emerald-100">
              <p className="font-semibold text-emerald-900">CARF Accredited</p>
              <p className="text-sm text-emerald-800 mt-1">Nationally recognized quality standards</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-6 border border-emerald-100">
              <p className="font-semibold text-emerald-900">VACCN Provider</p>
              <p className="text-sm text-emerald-800 mt-1">Approved VA Community Care Network partner</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-6 border border-emerald-100">
              <p className="font-semibold text-emerald-900">Medicaid & Medicare</p>
              <p className="text-sm text-emerald-800 mt-1">Broad insurance acceptance for easier access</p>
            </div>
          </div>
        </div>
      </div>

      {/* Who We Partner With */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Who We Partner With</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PARTNER_TYPES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-gray-200" data-testid={`partner-type-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <Icon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">How It Works</h2>
          <ol className="space-y-4">
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">1</span>
              <p className="text-gray-700">Submit a referral through our secure online form, listing OMHC, PRP, or both.</p>
            </li>
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">2</span>
              <p className="text-gray-700">Our intake team reviews the referral and required documentation.</p>
            </li>
            <li className="flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">3</span>
              <p className="text-gray-700">We reach out to the client directly to schedule intake and coordinate care with your team as needed.</p>
            </li>
          </ol>
        </div>
      </div>

      {/* Contact / CTA */}
      <div className="py-16 bg-emerald-50 border-t border-emerald-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Let's Start a Partnership</h2>
          <p className="text-gray-700 mb-6">
            Reach out to our intake team to discuss a referral partnership, or submit your first referral today.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <button
              onClick={() => setShowReferralForm(true)}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
              data-testid="partner-submit-referral-btn"
            >
              <Send className="w-4 h-4" /> Submit a Referral
            </button>
            <Link
              to="/providers/referral-requirements"
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              data-testid="partner-view-requirements-link"
            >
              View Referral Requirements
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <a href="tel:+18777725150" className="flex items-center gap-2 hover:text-emerald-700" data-testid="partner-phone-link">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <a href="mailto:info@sdorawellbeing.org" className="flex items-center gap-2 hover:text-emerald-700" data-testid="partner-email-link">
              <Mail className="w-4 h-4" /> info@sdorawellbeing.org
            </a>
          </div>
        </div>
      </div>

      <Footer />

      {showReferralForm && <ReferralFormModal onClose={() => setShowReferralForm(false)} />}
    </div>
  );
};

export default PartnerWithSdoraPage;
